import { useCallback, useEffect, useState } from 'react';
import {
    ArrowLeft,
    ArrowRight,
    Award,
    Check,
    HelpCircle,
    RotateCcw,
    Target,
    Timer,
    X,
} from 'lucide-react';
import Button from '../ui/Button';
import ProgressBar from '../ui/ProgressBar';
import EmptyState from '../ui/EmptyState';
import './QuizFlow.css';

export type QuizQuestion = {
    id: string;
    type: 'mcq' | 'text';
    question: string;
    options?: string[];
    correctAnswer: string;
    explanation: string;
};

export type Quiz = {
    id: string;
    title: string;
    description: string;
    courseId?: string;
    questions: QuizQuestion[];
    difficulty: 'Easy' | 'Medium' | 'Hard';
    timeLimit: number;
    passingScore: number;
};

export type QuizFlowProps = {
    quiz: Quiz;
    onExit: () => void;
};

function QuizFlow({ quiz, onExit }: QuizFlowProps) {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState<Record<string, string>>({});
    const [showResults, setShowResults] = useState(false);

    const questionCount = quiz.questions.length;
    const currentQuestion = quiz.questions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === questionCount - 1;

    const handleAnswer = useCallback((questionId: string, answer: string) => {
        setUserAnswers((prev) => ({ ...prev, [questionId]: answer }));
    }, []);

    const nextQuestion = useCallback(() => {
        setCurrentQuestionIndex((prev) => Math.min(prev + 1, questionCount - 1));
    }, [questionCount]);

    const prevQuestion = useCallback(() => {
        setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
    }, []);

    const submitQuiz = useCallback(() => {
        setShowResults(true);
    }, []);

    // Keyboard shortcuts — arrows to move between questions, 1–9 to pick MCQ
    // options, Enter to submit on the last question. Disabled while the text
    // input is focused so typing answers isn't intercepted.
    useEffect(() => {
        if (showResults || questionCount === 0) return;
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (target && target.tagName === 'INPUT') return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                prevQuestion();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (isLastQuestion) {
                    submitQuiz();
                } else {
                    nextQuestion();
                }
            } else if (e.key === 'Enter' && isLastQuestion) {
                e.preventDefault();
                submitQuiz();
            } else if (
                currentQuestion?.type === 'mcq' &&
                currentQuestion.options &&
                /^[1-9]$/.test(e.key)
            ) {
                const idx = parseInt(e.key, 10) - 1;
                const option = currentQuestion.options[idx];
                if (option !== undefined) {
                    e.preventDefault();
                    handleAnswer(currentQuestion.id, option);
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [
        showResults,
        questionCount,
        isLastQuestion,
        currentQuestion,
        prevQuestion,
        nextQuestion,
        submitQuiz,
        handleAnswer,
    ]);

    const calculateScore = () => {
        let correct = 0;
        quiz.questions.forEach((q) => {
            const userAnswer = userAnswers[q.id]?.toLowerCase().trim();
            const correctAnswer = q.correctAnswer.toLowerCase().trim();
            if (userAnswer === correctAnswer) {
                correct++;
            }
        });
        return Math.round((correct / quiz.questions.length) * 100);
    };

    const retryQuiz = () => {
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setShowResults(false);
    };

    if (questionCount === 0) {
        return (
            <div className="quiz-interface">
                <div className="quiz-header">
                    <Button
                        variant="ghost"
                        size="sm"
                        icon={<ArrowLeft size={18} />}
                        onClick={onExit}
                    >
                        Back to Lesson
                    </Button>
                </div>
                <EmptyState
                    icon={<HelpCircle size={28} />}
                    title="No questions yet"
                    description="This quiz doesn't have any questions to answer."
                />
            </div>
        );
    }

    return (
        <div className="quiz-interface">
            {/* Quiz Header */}
            <div className="quiz-header">
                <Button
                    variant="ghost"
                    size="sm"
                    icon={<ArrowLeft size={18} />}
                    onClick={onExit}
                    className="back-to-quizzes"
                >
                    Back to Lesson
                </Button>
                <h2 className="quiz-title">{quiz.title}</h2>
                <div className="quiz-meta-info">
                    <span className={`quiz-difficulty ${quiz.difficulty.toLowerCase()}`}>
                        {quiz.difficulty}
                    </span>
                    <span className="quiz-timer">
                        <Timer size={16} aria-hidden="true" />
                        {quiz.timeLimit} min
                    </span>
                </div>
            </div>

            {showResults ? (
                <div className="quiz-results">
                    <div className="results-card">
                        <div className="results-icon">
                            {calculateScore() >= quiz.passingScore ? (
                                <Award size={64} className="pass" />
                            ) : (
                                <Target size={64} className="fail" />
                            )}
                        </div>
                        <h3 className="results-title">
                            {calculateScore() >= quiz.passingScore
                                ? 'Congratulations!'
                                : 'Keep Practicing!'}
                        </h3>
                        <div className="score-display">
                            <span className="score-value">{calculateScore()}%</span>
                            <span className="score-label">Your Score</span>
                        </div>
                        <p className="score-message">
                            {calculateScore() >= quiz.passingScore
                                ? `You passed! Passing score is ${quiz.passingScore}%.`
                                : `You need ${quiz.passingScore}% to pass. Try again!`}
                        </p>
                        <div className="results-actions">
                            <Button variant="secondary" size="md" onClick={onExit}>
                                Back to Course
                            </Button>
                            <Button
                                variant="primary"
                                size="md"
                                icon={<RotateCcw size={18} />}
                                onClick={retryQuiz}
                            >
                                Retry Quiz
                            </Button>
                        </div>
                    </div>

                    {/* Answer Review */}
                    <div className="answer-review">
                        <h3 className="review-title">Review Your Answers</h3>
                        {quiz.questions.map((q, idx) => {
                            const userAnswer =
                                userAnswers[q.id]?.toLowerCase().trim() || '';
                            const isCorrect =
                                userAnswer === q.correctAnswer.toLowerCase().trim();
                            return (
                                <div
                                    key={q.id}
                                    className={`review-item ${
                                        isCorrect ? 'correct' : 'incorrect'
                                    }`}
                                >
                                    <div className="review-header">
                                        <span className="review-number">Q{idx + 1}</span>
                                        <span
                                            className={`review-status ${
                                                isCorrect ? 'correct' : 'incorrect'
                                            }`}
                                        >
                                            {isCorrect ? (
                                                <Check size={16} />
                                            ) : (
                                                <X size={16} />
                                            )}
                                            {isCorrect ? 'Correct' : 'Incorrect'}
                                        </span>
                                    </div>
                                    <p className="review-question">{q.question}</p>
                                    <div className="review-answers">
                                        <div className="your-answer">
                                            <span className="answer-label">
                                                Your answer:
                                            </span>
                                            <span
                                                className={`answer-text ${
                                                    isCorrect ? 'correct' : 'incorrect'
                                                }`}
                                            >
                                                {userAnswers[q.id] || '(No answer)'}
                                            </span>
                                        </div>
                                        {!isCorrect && (
                                            <div className="correct-answer">
                                                <span className="answer-label">
                                                    Correct answer:
                                                </span>
                                                <span className="answer-text correct">
                                                    {q.correctAnswer}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <p className="review-explanation">
                                        <strong>Explanation:</strong> {q.explanation}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="question-container">
                    <div className="question-progress">
                        <div className="progress-info">
                            <span>
                                Question {currentQuestionIndex + 1} of {questionCount}
                            </span>
                        </div>
                        <ProgressBar
                            value={((currentQuestionIndex + 1) / questionCount) * 100}
                            ariaLabel={`Question ${currentQuestionIndex + 1} of ${questionCount}`}
                        />
                    </div>

                    <div className="question-card">
                        <div className="question-type-badge">
                            {currentQuestion.type === 'mcq'
                                ? 'Multiple Choice'
                                : 'Short Answer'}
                        </div>
                        <h3 className="question-text">{currentQuestion.question}</h3>

                        {currentQuestion.type === 'mcq' && (
                            <div
                                className="mcq-options"
                                role="radiogroup"
                                aria-label="Answer options"
                            >
                                {currentQuestion.options?.map((option, idx) => {
                                    const isSelected =
                                        userAnswers[currentQuestion.id] === option;
                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            role="radio"
                                            aria-checked={isSelected ? 'true' : 'false'}
                                            className={`option-btn${
                                                isSelected ? ' option-btn--selected' : ''
                                            }`}
                                            onClick={() =>
                                                handleAnswer(currentQuestion.id, option)
                                            }
                                        >
                                            <span className="option-letter" aria-hidden="true">
                                                {String.fromCharCode(65 + idx)}
                                            </span>
                                            <span className="option-text">{option}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {currentQuestion.type === 'text' && (
                            <div className="text-answer">
                                <input
                                    type="text"
                                    className="answer-input"
                                    placeholder="Type your answer here..."
                                    value={userAnswers[currentQuestion.id] || ''}
                                    onChange={(e) =>
                                        handleAnswer(currentQuestion.id, e.target.value)
                                    }
                                />
                                <p className="input-hint">
                                    Enter your answer and press Enter or click Next
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="question-navigation">
                        <Button
                            variant="secondary"
                            size="md"
                            icon={<ArrowLeft size={18} />}
                            onClick={prevQuestion}
                            disabled={currentQuestionIndex === 0}
                        >
                            Previous
                        </Button>

                        {currentQuestionIndex === quiz.questions.length - 1 ? (
                            <Button
                                variant="primary"
                                size="md"
                                icon={<Check size={18} />}
                                iconPosition="right"
                                onClick={submitQuiz}
                            >
                                Submit Quiz
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                size="md"
                                icon={<ArrowRight size={18} />}
                                iconPosition="right"
                                onClick={nextQuestion}
                            >
                                Next
                            </Button>
                        )}
                    </div>

                    <div className="question-dots">
                        {quiz.questions.map((q, idx) => (
                            <button
                                key={q.id}
                                type="button"
                                className={`dot ${
                                    idx === currentQuestionIndex ? 'active' : ''
                                } ${userAnswers[q.id] ? 'answered' : ''}`}
                                onClick={() => setCurrentQuestionIndex(idx)}
                                aria-label={`Go to question ${idx + 1}`}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default QuizFlow;
